// Workaround for https://github.com/qq15725/modern-screenshot/issues/104:
// getComputedStyle returns px values rounded to 4-bit precision, which can push
// a line of text just past its container and trigger an unexpected wrap in the
// rendered image (Chrome only — Safari renders the same DOM correctly).
//
// Mitigation: scale inline font-size by 0.999 in the cloned DOM only, so the
// final px value carries enough fractional precision to avoid the boundary.
// Any element under a `[data-screenshot-font-fix]` ancestor is fixed, which
// lets us mark a whole rendered region once instead of every text leaf.
//
// Upstream proposed this fix in #104 (BugCreators, 2025-07-07); modern-screenshot
// shipped its own version in 4.5.1 but reverted it in 4.5.2 and never reapplied.
const FIX_SELECTOR = '[data-screenshot-font-fix]'
const SCALE = 0.999

export function scaleFontSizeFix(node: Node): void {
	if (!(node instanceof HTMLElement)) {
		return
	}
	if (!node.closest(FIX_SELECTOR)) {
		return
	}
	if (!node.style.fontSize) {
		return
	}
	node.style.fontSize = node.style.fontSize.replace(
		/(\d+(\.\d+)?(e[+-]?\d+)?)/g,
		(match, number) => {
			const parsed = Number.parseFloat(number)
			return Number.isNaN(parsed) ? match : (parsed * SCALE).toString()
		},
	)
}

// Every LiveField renders as a `[role="textbox"]`, regardless of engine or
// disabled state, so this catches all editable text in both ad editors.
const TEXTBOX_SELECTOR = '[role="textbox"]'

/**
 * Automates the user-discovered workaround of typing a trailing space, but only
 * for the duration of the capture so saved values stay untouched.
 *
 * The same sub-pixel rounding described above (#104) can push a line just past
 * its box and wrap it in the baked image only. A trailing space absorbs that:
 * with `white-space: pre-wrap` it either hangs off the end of a fixed-width
 * line (excluded from fit measurement) or, for a content-sized box such as a
 * CTA button hugging its label, widens the box by one space-width.
 *
 * The space MUST live on the live DOM, not the clone: modern-screenshot inlines
 * each box's width from `getComputedStyle` of the live element, so the box has
 * to already be measured with the extra space — a clone-only space would just
 * overflow the already-locked width and wrap anyway.
 */
export async function withTrailingSpacePadding<T>(
	root: Element,
	capture: () => Promise<T>,
): Promise<T> {
	const addedSpaces: Text[] = []

	for (const textbox of root.querySelectorAll<HTMLElement>(TEXTBOX_SELECTOR)) {
		const document = textbox.ownerDocument

		// A hanging space at the end of every visual line (segments split by <br>),
		// plus one at the very end for the final/only line.
		for (const child of Array.from(textbox.childNodes)) {
			if (child instanceof HTMLBRElement) {
				const space = document.createTextNode(' ')
				textbox.insertBefore(space, child)
				addedSpaces.push(space)
			}
		}
		const trailingSpace = document.createTextNode(' ')
		textbox.appendChild(trailingSpace)
		addedSpaces.push(trailingSpace)
	}

	try {
		return await capture()
	} finally {
		for (const space of addedSpaces) {
			space.remove()
		}
	}
}
